import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDailyPuzzle } from "@/utils/dailyPuzzle";

const WORD_LENGTH = 5;
const MAX_GUESSES = 6;
const FALLBACK = "SPOOF";

type LetterState = "correct" | "present" | "absent" | "empty" | "tbd";

function getTodayDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function fetchSolution(date: string): Promise<string> {
  const data = await fetchDailyPuzzle<{ solution?: string }>("word-drill", date);
  const solution = data?.solution;
  if (typeof solution === "string" && solution.length === WORD_LENGTH) {
    return solution.toUpperCase();
  }
  return FALLBACK;
}

function evaluateGuess(guess: string, solution: string): LetterState[] {
  const result: LetterState[] = Array(WORD_LENGTH).fill("absent");
  const solutionArr = solution.split("");
  const guessArr = guess.split("");
  const used = Array(WORD_LENGTH).fill(false);

  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessArr[i] === solutionArr[i]) {
      result[i] = "correct";
      used[i] = true;
    }
  }
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === "correct") continue;
    for (let j = 0; j < WORD_LENGTH; j++) {
      if (!used[j] && guessArr[i] === solutionArr[j]) {
        result[i] = "present";
        used[j] = true;
        break;
      }
    }
  }
  return result;
}

const KEYBOARD_ROWS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"],
];

type SavedState = {
  date: string;
  guesses: string[];
  status: "playing" | "won" | "lost";
};

function loadState(date: string): SavedState {
  if (typeof window === "undefined") return { date, guesses: [], status: "playing" };
  try {
    const raw = localStorage.getItem("wordle-state");
    if (!raw) return { date, guesses: [], status: "playing" };
    const parsed = JSON.parse(raw) as SavedState;
    if (parsed.date !== date) return { date, guesses: [], status: "playing" };
    return parsed;
  } catch {
    return { date, guesses: [], status: "playing" };
  }
}

export default function WordDrill() {
  const today = useMemo(() => getTodayDate(), []);
  const [solution, setSolution] = useState<string | null>(null);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [current, setCurrent] = useState("");
  const [status, setStatus] = useState<"playing" | "won" | "lost">("playing");
  const [shake, setShake] = useState(false);
  const [revealRow, setRevealRow] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetchSolution(today).then(setSolution);
    const saved = loadState(today);
    setGuesses(saved.guesses);
    setStatus(saved.status);
  }, [today]);

  useEffect(() => {
    if (solution === null) return;
    localStorage.setItem(
      "wordle-state",
      JSON.stringify({ date: today, guesses, status }),
    );
  }, [today, guesses, status, solution]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1800);
  }, []);

  const submitGuess = useCallback(() => {
    if (!solution) return;
    if (current.length !== WORD_LENGTH) {
      setShake(true);
      showToast("Not enough letters");
      setTimeout(() => setShake(false), 500);
      return;
    }
    const newGuesses = [...guesses, current];
    setGuesses(newGuesses);
    setRevealRow(newGuesses.length - 1);
    setCurrent("");
    setTimeout(() => setRevealRow(null), 1500);
    if (current === solution) {
      setStatus("won");
      setTimeout(() => showToast("Magnificent!"), 1600);
    } else if (newGuesses.length >= MAX_GUESSES) {
      setStatus("lost");
      setTimeout(() => showToast(solution), 1600);
    }
  }, [current, guesses, solution, showToast]);

  const handleKey = useCallback(
    (key: string) => {
      if (status !== "playing" || !solution) return;
      if (key === "ENTER") {
        submitGuess();
      } else if (key === "BACKSPACE") {
        setCurrent((c) => c.slice(0, -1));
      } else if (/^[A-Z]$/.test(key) && current.length < WORD_LENGTH) {
        setCurrent((c) => c + key);
      }
    },
    [current.length, status, solution, submitGuess],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toUpperCase();
      if (k === "ENTER" || k === "BACKSPACE" || /^[A-Z]$/.test(k)) {
        e.preventDefault();
        handleKey(k);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleKey]);

  const keyStates = useMemo(() => {
    const map: Record<string, LetterState> = {};
    if (!solution) return map;
    const rank = { absent: 0, present: 1, correct: 2 } as const;
    for (const g of guesses) {
      const ev = evaluateGuess(g, solution);
      for (let i = 0; i < WORD_LENGTH; i++) {
        const ch = g[i];
        const s = ev[i] as "absent" | "present" | "correct";
        const prev = map[ch];
        if (!prev || rank[s] > rank[prev as "absent" | "present" | "correct"]) {
          map[ch] = s;
        }
      }
    }
    return map;
  }, [guesses, solution]);

  const rows = Array.from({ length: MAX_GUESSES }, (_, r) => {
    if (r < guesses.length) {
      return { letters: guesses[r].split(""), states: solution ? evaluateGuess(guesses[r], solution) : [], submitted: true };
    }
    if (r === guesses.length && status === "playing") {
      const padded = current.padEnd(WORD_LENGTH, " ").split("");
      return { letters: padded, states: padded.map(() => "tbd" as LetterState), submitted: false };
    }
    return { letters: Array(WORD_LENGTH).fill(" "), states: Array(WORD_LENGTH).fill("empty" as LetterState), submitted: false };
  });

  const tileColor = (s: LetterState) => {
    switch (s) {
      case "correct": return "bg-[hsl(var(--wordle-correct))] text-[hsl(0_0%_97%)] border-[hsl(var(--wordle-correct))]";
      case "present": return "bg-[hsl(var(--wordle-present))] text-[hsl(0_0%_97%)] border-[hsl(var(--wordle-present))]";
      case "absent": return "bg-[hsl(var(--wordle-absent))] text-[hsl(0_0%_97%)] border-[hsl(var(--wordle-absent))]";
      case "tbd": return "bg-background text-foreground border-[hsl(var(--wordle-tile-filled-border))]";
      default: return "bg-background text-foreground border-[hsl(var(--wordle-tile-border))]";
    }
  };

  const keyColor = (k: string) => {
    const s = keyStates[k];
    if (s === "correct") return "bg-[hsl(var(--wordle-correct))] text-[hsl(0_0%_97%)]";
    if (s === "present") return "bg-[hsl(var(--wordle-present))] text-[hsl(0_0%_97%)]";
    if (s === "absent") return "bg-[hsl(var(--wordle-absent))] text-[hsl(0_0%_97%)]";
    return "bg-[hsl(var(--wordle-key))] text-[hsl(0_0%_15%)]";
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <Link to="/vocab-lab" className="text-sm font-semibold text-muted-foreground hover:text-foreground">← Lab</Link>
          <h1 className="text-3xl font-bold tracking-[0.2em] text-foreground">WORD DRILL</h1>
          <Link to="/vocab-lab/group-sort" className="text-sm font-semibold text-muted-foreground hover:text-foreground">Group Sort →</Link>
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-between gap-6 px-2 py-4">
        <div className="relative flex w-full justify-center">
          {toast && (
            <div className="absolute top-0 z-10 rounded-sm bg-foreground px-4 py-3 text-sm font-bold uppercase text-background shadow-lg">
              {toast}
            </div>
          )}
        </div>

        <div className={`grid grid-rows-6 gap-1.5 ${shake ? "animate-[shake_0.5s]" : ""}`}>
          {rows.map((row, ri) => (
            <div key={ri} className="grid grid-cols-5 gap-1.5">
              {row.letters.map((ch, ci) => {
                const state = row.states[ci] ?? "empty";
                const isReveal = revealRow === ri;
                const filled = ch && ch !== " ";
                return (
                  <div
                    key={ci}
                    className={`flex h-14 w-14 items-center justify-center border-2 text-2xl font-bold uppercase transition-transform sm:h-16 sm:w-16 sm:text-3xl ${tileColor(state)} ${filled && state === "tbd" ? "scale-[1.05]" : ""} ${isReveal ? "animate-[flip_0.6s_ease]" : ""}`}
                    style={isReveal ? { animationDelay: `${ci * 0.1}s` } : undefined}
                  >
                    {ch !== " " ? ch : ""}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex w-full max-w-lg flex-col gap-1.5 px-1">
          {KEYBOARD_ROWS.map((row, i) => (
            <div key={i} className="flex justify-center gap-1.5">
              {i === 1 && <div className="flex-[0.5]" />}
              {row.map((k) => {
                const wide = k === "ENTER" || k === "BACKSPACE";
                return (
                  <button
                    key={k}
                    onClick={() => handleKey(k)}
                    className={`flex h-14 items-center justify-center rounded font-bold uppercase transition-colors active:scale-95 ${keyColor(k)} ${wide ? "flex-[1.5] text-xs" : "flex-1 text-sm"}`}
                  >
                    {k === "BACKSPACE" ? "⌫" : k}
                  </button>
                );
              })}
              {i === 1 && <div className="flex-[0.5]" />}
            </div>
          ))}
        </div>
      </main>

      <style>{`
        @keyframes flip {
          0% { transform: rotateX(0); }
          50% { transform: rotateX(90deg); }
          100% { transform: rotateX(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}